/**
 * API base URL — the Classic ASP router at admin.entourageyearbooks.com/app/.
 * Configured per environment via VITE_API_BASE_URL. Trailing slash optional.
 */
const API_BASE = (
  import.meta.env.VITE_API_BASE_URL ?? 'https://admin.entourageyearbooks.com/app/'
).replace(/\/+$/, '');

/**
 * The React app is hosted on a different origin than the ASP backend
 * (Railway vs. admin.entourageyearbooks.com), so the HTTP Host header
 * the backend sees doesn't identify the brand. We pass the current
 * client-side hostname explicitly, and the ASP controller uses it for
 * the host -> company_id mapping.
 */
function clientHost(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname;
}

function apiUrl(params: Record<string, string>): string {
  const search = new URLSearchParams({ host: clientHost(), ...params }).toString();
  return `${API_BASE}/?${search}`;
}

export interface Brand {
  brandName: string;
  logoUrl: string;
  iconUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  contactEmail: string;
}

export interface OrgInfo {
  orgId: string;
  orgName: string;
  orgAddress: string;
  orgAddress2: string;
  orgCity: string;
  orgState: string;
  orgZip: string;
  orgCountry: string;
}

export interface OrgAddressInput {
  orgAddress: string;
  orgAddress2: string;
  orgCity: string;
  orgState: string;
  orgZip: string;
  orgCountry: string;
}

export interface SubmitResult {
  success: boolean;
  ticket_id?: number;
}

export async function fetchBrand(): Promise<Brand> {
  const res = await fetch(
    apiUrl({ ctrler: 'TaxExemptCompany', method: 'index' }),
  );
  if (!res.ok) throw new Error(`brand_${res.status}`);
  return (await res.json()) as Brand;
}

export async function fetchOrgInfo(orgId: string, token: string): Promise<OrgInfo> {
  const res = await fetch(
    apiUrl({ ctrler: 'TaxExemptOrg', method: 'index', org_id: orgId, token }),
  );
  if (!res.ok) throw new Error(`org_${res.status}`);
  return (await res.json()) as OrgInfo;
}

// Unified submit: address fields + optional PDF in one multipart request.
// Multipart/form-data is a CORS-safelisted content type, so the request
// avoids the preflight that the previous JSON-POST submitAddress endpoint
// triggered (and which IIS's DefaultDocumentModule didn't answer with the
// right CORS headers).
export async function submitTaxExemptForm(
  orgId: string,
  token: string,
  address: OrgAddressInput,
  file: File | null,
): Promise<SubmitResult> {
  const fd = new FormData();
  fd.append('org_id', orgId);
  fd.append('orgAddress', address.orgAddress);
  fd.append('orgAddress2', address.orgAddress2);
  fd.append('orgCity', address.orgCity);
  fd.append('orgState', address.orgState);
  fd.append('orgZip', address.orgZip);
  fd.append('orgCountry', address.orgCountry);
  if (file) {
    fd.append('file', file);
  }
  const res = await fetch(
    apiUrl({ ctrler: 'TaxExemptTicket', method: 'submit', token }),
    { method: 'POST', body: fd },
  );
  if (!res.ok) throw new Error(`submit_${res.status}`);
  return (await res.json()) as SubmitResult;
}
